import React, { useState, useEffect } from 'react';
const API = '/api';
const BRAND = { olive:'#556B2F', cream:'#F2EFE7', gold:'#A8893D', brown:'#3B2A21', red:'#8F2E24' };

export default function OfferView({ token }) {
  const [loading, setLoading] = useState(true);
  const [offer, setOffer] = useState(null);
  const [error, setError] = useState('');
  const [stage, setStage] = useState('view');
  const [form, setForm] = useState({ full_name: '', consent: false });
  const [saving, setSaving] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    fetch(`${API}/portal/offer/${token}`)
      .then(r => r.json())
      .then(d => { if (d.error) setError(d.error); else setOffer(d); })
      .catch(() => setError('Failed to load offer. Please try again.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleScroll = (e) => {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop <= el.clientHeight + 100) setScrolled(true);
  };

  const checkScrollNeeded = (el) => {
    if (el && el.scrollHeight <= el.clientHeight + 50) setScrolled(true);
  };

  const respond = async (decision) => {
    if (decision === 'accept') {
      if (!form.full_name.trim()) { setError('Please enter your full legal name to accept'); return; }
      if (!form.consent) { setError('Please confirm you have read and understood the offer'); return; }
      if (!scrolled) { setError('Please scroll through the entire offer letter before accepting'); return; }
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/portal/offer/${token}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, full_name: form.full_name })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error); return; }
      setStage(decision === 'accept' ? 'accepted' : 'declined');
    } catch(e) { setError('Something went wrong. Please try again.'); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div style={{minHeight:'60vh',display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{color:BRAND.olive,fontSize:16}}>Loading your offer letter...</div>
    </div>
  );

  if (stage === 'accepted') return (
    <div style={{maxWidth:560,margin:'40px auto',padding:24,textAlign:'center'}}>
      <div style={{background:'#fff',borderRadius:16,padding:40,boxShadow:'0 4px 24px rgba(0,0,0,.08)'}}>
        <div style={{fontSize:56,marginBottom:16}}>🎉</div>
        <h2 style={{fontFamily:'Georgia,serif',fontSize:'1.8rem',color:'#2E6B3A',marginBottom:12}}>Offer Accepted!</h2>
        <p style={{color:'#555',lineHeight:1.7,marginBottom:16}}>
          Welcome to Bloans! Your offer has been accepted and recorded. HR will be in touch with your next steps shortly.
        </p>
        <div style={{background:'#E8F5EB',borderRadius:10,padding:16,fontSize:13,color:'#2E6B3A'}}>
          Please check your email for further instructions on completing your onboarding.
        </div>
      </div>
    </div>
  );

  if (stage === 'declined') return (
    <div style={{maxWidth:560,margin:'40px auto',padding:24,textAlign:'center'}}>
      <div style={{background:'#fff',borderRadius:16,padding:40,boxShadow:'0 4px 24px rgba(0,0,0,.08)'}}>
        <div style={{fontSize:56,marginBottom:16}}>👋</div>
        <h2 style={{fontFamily:'Georgia,serif',fontSize:'1.8rem',color:BRAND.brown,marginBottom:12}}>Offer Declined</h2>
        <p style={{color:'#555',lineHeight:1.7}}>
          Your decision has been recorded. Thank you for your time and consideration. We wish you all the best.
        </p>
      </div>
    </div>
  );

  if (error && !offer) return (
    <div style={{maxWidth:560,margin:'40px auto',padding:24,textAlign:'center'}}>
      <div style={{background:'#fff',borderRadius:16,padding:40,boxShadow:'0 4px 24px rgba(0,0,0,.08)'}}>
        <div style={{fontSize:48,marginBottom:16}}>⚠️</div>
        <h2 style={{fontFamily:'Georgia,serif',fontSize:'1.8rem',color:BRAND.red,marginBottom:12}}>{error}</h2>
        <p style={{color:'#8A8A8A',fontSize:13}}>Please contact HR at <a href="mailto:hr@bloanshandbags.com" style={{color:BRAND.olive}}>hr@bloanshandbags.com</a></p>
      </div>
    </div>
  );

  return (
    <div style={{maxWidth:800,margin:'0 auto',padding:'24px 16px'}}>

      {/* Header notice */}
      <div style={{background:BRAND.brown,borderRadius:10,padding:'14px 20px',marginBottom:20,
        display:'flex',alignItems:'center',gap:12}}>
        <div style={{flex:1}}>
          <div style={{color:BRAND.gold,fontSize:13,fontWeight:700}}>Offer of Employment — {offer?.position}</div>
          <div style={{color:'rgba(242,239,231,.6)',fontSize:11,marginTop:2}}>
            Please read the full offer letter carefully before accepting or declining.
            {offer?.expiry_date && ` This offer expires on ${new Date(offer.expiry_date).toLocaleDateString('en-NG',{day:'numeric',month:'long',year:'numeric'})}.`}
          </div>
        </div>
      </div>

      {stage === 'view' && (
        <>
          {/* Full merged offer letter */}
          {offer?.merged_content ? (
            <div style={{background:'#fff',borderRadius:12,boxShadow:'0 2px 16px rgba(0,0,0,.06)',
              marginBottom:20,maxHeight:'70vh',overflowY:'auto',padding:8}}
              onScroll={handleScroll}
              ref={checkScrollNeeded}>
              <div dangerouslySetInnerHTML={{__html: offer.merged_content}} />
            </div>
          ) : (
            /* Fallback basic view if no template */
            <div style={{background:'#fff',borderRadius:12,padding:24,marginBottom:20,
              boxShadow:'0 2px 16px rgba(0,0,0,.06)'}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:20}}>
                {[
                  ['Applicant', offer?.prospect_name],
                  ['Position', offer?.position],
                  ['Department', offer?.department],
                  ['Reports To', offer?.reports_to||'—'],
                  ['Start Date', offer?.proposed_start_date ? new Date(offer.proposed_start_date).toLocaleDateString('en-NG',{day:'numeric',month:'long',year:'numeric'}) : '—'],
                  ['Salary', offer?.monthly_salary ? '₦'+Number(offer.monthly_salary).toLocaleString('en-NG')+' / month' : '—'],
                  ['Probation', offer?.probation_period||'3 months'],
                  ['Location', offer?.work_location||'Ibadan, Nigeria'],
                ].map(([label, val]) => (
                  <div key={label} style={{background:'#F9F8F5',borderRadius:8,padding:12}}>
                    <div style={{fontSize:10,color:'#8A8A8A',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:3}}>{label}</div>
                    <div style={{fontSize:13,fontWeight:600,color:BRAND.brown}}>{val||'—'}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!scrolled && offer?.merged_content && (
            <div style={{background:'#FFF3DC',border:'1px solid #F0D9A0',borderRadius:8,
              padding:'10px 16px',marginBottom:16,fontSize:12,color:'#8B5E0A',textAlign:'center'}}>
              ⬇ Please scroll through the entire offer letter before accepting
            </div>
          )}

          {error && <div style={{background:'#FDEAEA',border:'1px solid #F5B8B8',borderRadius:8,
            padding:'10px 16px',marginBottom:16,fontSize:13,color:BRAND.red}}>{error}</div>}

          <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
            <button onClick={()=>respond('decline')} disabled={saving}
              style={{padding:'12px 24px',borderRadius:10,border:'1px solid '+BRAND.red,
                background:'#fff',color:BRAND.red,cursor:'pointer',fontSize:14,fontWeight:600}}>
              Decline Offer
            </button>
            <button onClick={()=>{setError('');setStage('confirm');}} 
              style={{padding:'12px 28px',borderRadius:10,border:'none',
                background:BRAND.olive,color:'#fff',cursor:'pointer',fontSize:14,fontWeight:600}}>
              Accept Offer →
            </button>
          </div>
        </>
      )}

      {stage === 'confirm' && (
        <div style={{background:'#fff',borderRadius:12,padding:28,boxShadow:'0 2px 16px rgba(0,0,0,.06)'}}>
          <h3 style={{fontFamily:'Georgia,serif',fontSize:'1.3rem',color:BRAND.brown,marginBottom:20}}>
            Confirm Your Acceptance
          </h3>

          <div style={{background:'#F2EFE7',borderRadius:8,padding:16,marginBottom:20,fontSize:13,color:BRAND.brown,lineHeight:1.8}}>
            By accepting this offer, I confirm that I have read and understood the Offer Letter in full. I accept the offer subject to its stated conditions and confirm that the information I have supplied or will supply to Bloans is true and complete.
          </div>

          <div style={{marginBottom:16}}>
            <label style={{display:'block',fontSize:12,fontWeight:600,color:'#8A8A8A',
              textTransform:'uppercase',letterSpacing:'.06em',marginBottom:6}}>
              Your Full Legal Name *
            </label>
            <input style={{width:'100%',padding:'10px 14px',border:'1px solid #E8E0D4',
              borderRadius:8,fontSize:14,fontFamily:'Georgia,serif',color:BRAND.brown,
              background:'#fff',boxSizing:'border-box'}}
              value={form.full_name}
              onChange={e=>setForm({...form,full_name:e.target.value})}
              placeholder="Type your full legal name exactly as it appears on your ID" />
          </div>

          <label style={{display:'flex',alignItems:'flex-start',gap:10,cursor:'pointer',marginBottom:24}}>
            <input type="checkbox" style={{accentColor:BRAND.olive,width:18,height:18,
              flexShrink:0,marginTop:2}}
              checked={form.consent}
              onChange={e=>setForm({...form,consent:e.target.checked})} />
            <span style={{fontSize:13,color:BRAND.brown,lineHeight:1.7}}>
              I confirm that I have read and understood the full Offer Letter, including all terms and conditions, and I accept this offer of employment with Bloans Creations Ltd.
            </span>
          </label>

          {error && <div style={{background:'#FDEAEA',border:'1px solid #F5B8B8',borderRadius:8,
            padding:'10px 16px',marginBottom:16,fontSize:13,color:BRAND.red}}>{error}</div>}

          <div style={{display:'flex',gap:10,justifyContent:'space-between'}}>
            <button onClick={()=>setStage('view')}
              style={{padding:'12px 24px',borderRadius:10,border:'1px solid #E8E0D4',
                background:'#fff',color:BRAND.brown,cursor:'pointer',fontSize:14}}>
              ← Back to Offer
            </button>
            <button onClick={()=>respond('accept')} disabled={saving}
              style={{padding:'12px 28px',borderRadius:10,border:'none',
                background:saving?'#ccc':BRAND.olive,color:'#fff',
                cursor:saving?'not-allowed':'pointer',fontSize:14,fontWeight:700}}>
              {saving ? 'Submitting...' : 'Confirm Acceptance'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
